import {type FilterQuery, SubmitEvent} from '@github-ui/filter'
import {useSearchParams} from '@github-ui/react-router'
import {useSyncedState} from '@github-ui/use-synced-state'
import {useCallback} from 'react'
import {useActionsAnalytics} from '../use-actions-analytics'
import {
  removeReactToggleSearchParam,
  removeWorkflowRunsPageSearchParam,
  resolveWorkflowRunsQueryOptions,
} from '../../queries/workflow-runs/workflow-runs-query'

export function updateWorkflowRunsSearchParams(current: URLSearchParams, query: string) {
  const next = removeWorkflowRunsPageSearchParam(current)
  const normalizedQuery = query.trim()

  if (normalizedQuery) {
    next.set('query', normalizedQuery)
  } else {
    next.delete('query')
  }

  return next
}

export function updateWorkflowRunsPageSearchParams(current: URLSearchParams, page: number) {
  const next = removeReactToggleSearchParam(current)

  if (Number.isInteger(page) && page > 1) {
    next.set('page', String(page))
  } else {
    next.delete('page')
  }

  return next
}

export function useWorkflowRunsSearch() {
  const track = useActionsAnalytics()
  const [searchParams, setSearchParams] = useSearchParams()
  const {query, page} = resolveWorkflowRunsQueryOptions({params: {}, searchParams})
  const [filterValue, setFilterValue] = useSyncedState(query)

  const updateQuery = useCallback(
    (nextQuery: string) => {
      // Focus should stay in the filter input while typing, not jump to the page title.
      setSearchParams(current => updateWorkflowRunsSearchParams(current, nextQuery), {preventAutofocus: true})
    },
    [setSearchParams],
  )

  const updatePage = useCallback(
    (nextPage: number) => {
      // WorkflowRunsPage moves focus once the new page loads, so don't also focus the title.
      setSearchParams(current => updateWorkflowRunsPageSearchParams(current, nextPage), {preventAutofocus: true})
    },
    [setSearchParams],
  )
  const pageHref = useCallback(
    (nextPage: number) => {
      const next = updateWorkflowRunsPageSearchParams(searchParams, nextPage).toString()
      return next ? `?${next}` : '?'
    },
    [searchParams],
  )
  const filterHref = useCallback(
    (nextQuery: string) => {
      const next = updateWorkflowRunsSearchParams(searchParams, nextQuery).toString()
      return next ? `?${next}` : '?'
    },
    [searchParams],
  )

  const handleChange = useCallback(
    (value: string) => {
      setFilterValue(value)
      if (!value.trim() && query) updateQuery('')
    },
    [query, setFilterValue, updateQuery],
  )

  const handleSubmit = useCallback(
    (filterQuery: FilterQuery, eventType: SubmitEvent) => {
      if (eventType === SubmitEvent.Clear) {
        // Filter calls onChange('') before this callback; handleChange already resets an applied query.
        if (filterValue.trim() && !filterQuery.raw.trim()) {
          track('workflow_runs.clear_search', 'ref_loc:actions_workflow_runs')
        }
        return
      }
      updateQuery(filterQuery.raw)
    },
    [filterValue, track, updateQuery],
  )
  const replaceQuery = useCallback(
    (nextQuery: string) => {
      setFilterValue(nextQuery)
      updateQuery(nextQuery)
    },
    [setFilterValue, updateQuery],
  )

  return {query, page, filterValue, handleChange, handleSubmit, replaceQuery, updatePage, pageHref, filterHref}
}
